import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { formatDate } from '../../utils/dateFormatting';
import { useParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  ArrowLeft, Building2, Users, HardHat, Briefcase, Settings,
  Ticket, FileText, MessageSquare, UserCog, Save, Loader2, ExternalLink,
  Link, Unlink, Search, CreditCard, KeyRound, Copy, Check, Send, Clock, AlertTriangle, Star,
  FileCheck, Receipt, FileSignature, ClipboardList, Activity, TrendingUp, TrendingDown, Phone,
  Mail, MailCheck, Plus, Trash2, Package
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { Switch } from '../../components/ui/switch';

// Tier → features mapping for the override toggles
const TIER_FEATURES = {
  advanced: [
    { id: 'standard_reports', label: 'Standard Reports' },
    { id: 'forms', label: 'Forms' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'timesheets', label: 'Timesheets' },
    { id: 'messaging', label: 'Messaging' },
    { id: 'quotes', label: 'Quotes' },
    { id: 'data_import', label: 'Data Import' },
    { id: 'payroll_export', label: 'Payroll Export' },
    { id: 'worker_availability', label: 'Worker Availability' },
    { id: 'compliance_packs', label: 'Compliance Packs' },
  ],
  professional: [
    { id: 'premium_reports', label: 'Premium Reports' },
    { id: 'quote_templates', label: 'Quote Templates' },
    { id: 'quote_analytics', label: 'Quote Analytics' },
    { id: 'ai_features', label: 'AI Features' },
    { id: 'smart_suggestions', label: 'Smart Suggestions' },
    { id: 'ocr_dockets', label: 'OCR Docket Scanning' },
    { id: 'api_access', label: 'API Access' },
  ],
};

const FeatureOverridesCard = ({ tier, overrides, onToggle, isDark }) => {
  // Only show features from higher tiers than the tenant's current tier
  const tierOrder = ['basic', 'advanced', 'professional'];
  const currentIdx = tierOrder.indexOf(tier);

  const sections = [];
  if (currentIdx < 1) {
    sections.push({ tierName: 'Advanced', features: TIER_FEATURES.advanced });
  }
  if (currentIdx < 2) {
    sections.push({ tierName: 'Professional', features: TIER_FEATURES.professional });
  }

  if (sections.length === 0) return null; // Professional tier has all features

  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
      <CardHeader className="py-3">
        <CardTitle className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
          <Star className="h-4 w-4 text-[#ffd108]" />
          Feature Overrides
          {overrides.length > 0 && (
            <Badge className="bg-[#ffd108]/20 text-[#ffd108] border border-[#ffd108]/30 text-xs ml-auto">{overrides.length} active</Badge>
          )}
        </CardTitle>
        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
          Grant individual features from higher tiers without upgrading the subscription
        </p>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {sections.map(({ tierName, features }) => (
          <div key={tierName}>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              {tierName} Features
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {features.map(f => (
                <label key={f.id} className={`flex items-center justify-between gap-2 py-1.5 px-2 rounded-md cursor-pointer ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
                  <span className={`text-sm ${overrides.includes(f.id) ? (isDark ? 'text-white' : 'text-gray-900') : (isDark ? 'text-slate-400' : 'text-gray-500')}`}>
                    {f.label}
                  </span>
                  <Switch
                    checked={overrides.includes(f.id)}
                    onCheckedChange={() => onToggle(f.id)}
                    data-testid={`feature-override-${f.id}`}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// StatCard moved outside component to prevent re-creation on each render
const formatRelativeTime = (iso) => {
  if (!iso) return 'No activity';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  if (isNaN(diffMs)) return 'No activity';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

// Churn-risk color cue for "Last Activity": amber > 14d, red > 30d
const lastActivityColor = (iso) => {
  if (!iso) return 'text-red-400';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (isNaN(days)) return 'text-red-400';
  if (days > 30) return 'text-red-400';
  if (days > 14) return 'text-amber-400';
  return undefined;
};

const StatCard = ({ title, value, icon: Icon, color = 'text-[#ffd108]', isDark = true, delta, valueColor, testId }) => (
  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-4`} data-testid={testId}>
    <div className="flex items-center gap-3">
      <Icon className={`h-5 w-5 ${color}`} />
      <div>
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{title}</p>
        <div className="flex items-center gap-2">
          <p className={`text-xl font-semibold ${valueColor || (isDark ? 'text-white' : 'text-gray-900')}`}>{value}</p>
          {delta !== undefined && delta !== null && (
            <span
              className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${
                delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : (isDark ? 'text-slate-400' : 'text-gray-400')
              }`}
              title="vs prior 30 days"
              data-testid={testId ? `${testId}-delta` : undefined}
            >
              {delta > 0 ? <TrendingUp className="h-3 w-3" /> : delta < 0 ? <TrendingDown className="h-3 w-3" /> : null}
              {delta > 0 ? '+' : ''}{delta}%
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

const TenantDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [featureOverrides, setFeatureOverrides] = useState([]);
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Password reset state
  const [passwordResetDialog, setPasswordResetDialog] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);

  // Role edit state
  const [roleEditDialog, setRoleEditDialog] = useState(false);
  const [roleEditUser, setRoleEditUser] = useState(null);
  const [roleEditData, setRoleEditData] = useState(null);
  const [roleEditLoading, setRoleEditLoading] = useState(false);
  const [roleEditSaving, setRoleEditSaving] = useState(false);
  
  // Stripe linking state
  const [stripeLinkDialog, setStripeLinkDialog] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [stripeSearchQuery, setStripeSearchQuery] = useState('');
  const [stripeSearchResults, setStripeSearchResults] = useState([]);
  const [stripeSearching, setStripeSearching] = useState(false);
  const [stripeLinking, setStripeLinking] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  
  // Subscription invite state
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteNote, setInviteNote] = useState('');
  const [inviteCoupon, setInviteCoupon] = useState('');
  const [additionalProducts, setAdditionalProducts] = useState([]); // [{ price_id, product_name, unit_amount, currency, recurring, type }]
  const [inviteCurrency, setInviteCurrency] = useState('aud');
  const [priceIdInput, setPriceIdInput] = useState('');
  const [priceLookupLoading, setPriceLookupLoading] = useState(false);
  const [currencyValidation, setCurrencyValidation] = useState(null); // { results, all_supported, message }
  const [currencyValidating, setCurrencyValidating] = useState(false);
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendDays, setExtendDays] = useState(7);

  // Resend user invite state
  const [resendingInvite, setResendingInvite] = useState(null); // user id being sent
  
  // Demo data state
  const [demoDataStatus, setDemoDataStatus] = useState(null);
  const [demoSeeding, setDemoSeeding] = useState(false);
  const [demoPurging, setDemoPurging] = useState(false);
  const [demoPurgeConfirm, setDemoPurgeConfirm] = useState(false);
  const [seedFileSelect, setSeedFileSelect] = useState('');
  const [seedFiles, setSeedFiles] = useState([]);

  // Permanent delete state
  const [permDeleteOpen, setPermDeleteOpen] = useState(false);
  const [permDeleteConfirmText, setPermDeleteConfirmText] = useState('');
  const [permDeleting, setPermDeleting] = useState(false);
  
  // Live Stripe billing summary
  const [stripeSummary, setStripeSummary] = useState(null);
  const [stripeSummaryLoading, setStripeSummaryLoading] = useState(false);
  
  // Check if current user is admin (not support agent)
  const isAdmin = admin?.role === 'admin';
  // Support agents must not see monetary figures (MRR).
  const showMRR = admin?.role !== 'support';

  const fetchTenant = useCallback(async () => {
    try {
      const res = await api.get(`/api/super-admin/tenants/${clientId}`);
      setTenant(res.data);
      setEditData({
        tier: res.data.tier || 'basic',
        subscription_status: res.data.subscription_status || 'active',
        extra_operators: res.data.extra_operators || 0,
        extra_operators_timesheet: res.data.extra_operators_timesheet || 0,
        extra_users: res.data.extra_users || 0,
        ai_enabled: res.data.ai_enabled || false,
        early_adopter: res.data.early_adopter || false,
        estimated_mrr: res.data.estimated_mrr ?? '',
        phone_support: res.data.phone_support || false
      });
      setFeatureOverrides(res.data.feature_overrides || []);
    } catch (err) {
      console.error('Failed to fetch tenant:', err);
    } finally {
      setLoading(false);
    }
  }, [api, clientId]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  // Fetch live Stripe billing summary when tenant has a Stripe customer
  const fetchStripeSummary = useCallback(async () => {
    if (!clientId) return;
    setStripeSummaryLoading(true);
    try {
      const res = await api.get(`/api/super-admin/tenants/${clientId}/stripe-summary`);
      setStripeSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch Stripe summary:', err);
    } finally {
      setStripeSummaryLoading(false);
    }
  }, [api, clientId]);

  useEffect(() => {
    if (tenant?.stripe_customer_id) {
      fetchStripeSummary();
    }
  }, [tenant?.stripe_customer_id, fetchStripeSummary]);

  // ── Demo Data helpers ──────────────────────────────────────────
  const fetchDemoDataStatus = useCallback(async () => {
    try {
      const res = await api.get(`/api/super-admin/tenants/${clientId}/demo-data-status`);
      setDemoDataStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch demo data status:', err);
    }
  }, [api, clientId]);

  const fetchSeedFiles = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/demo-tenants/seed-files');
      setSeedFiles(res.data.seed_files || []);
    } catch (err) { /* non-critical */ }
  }, [api]);

  useEffect(() => {
    if (clientId) {
      fetchDemoDataStatus();
      fetchSeedFiles();
    }
  }, [clientId, fetchDemoDataStatus, fetchSeedFiles]);

  const handleSeedDemoData = async () => {
    setDemoSeeding(true);
    try {
      const body = {};
      if (seedFileSelect) body.seed_file_name = seedFileSelect;
      const res = await api.post(`/api/super-admin/tenants/${clientId}/seed-demo-data`, body);
      toast.success(`Seeded ${Object.values(res.data.stats || {}).reduce((a, b) => a + b, 0)} demo records`);
      fetchDemoDataStatus();
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setDemoSeeding(false);
    }
  };

  const handlePurgeDemoData = async () => {
    setDemoPurging(true);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/purge-demo-data`);
      const purgedTotal = Object.values(res.data.purged || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purged ${purgedTotal} demo records`);
      fetchDemoDataStatus();
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purge demo data');
    } finally {
      setDemoPurging(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tenants/${clientId}`, { ...editData, feature_overrides: featureOverrides });
      setTenant(prev => ({ ...prev, ...editData, feature_overrides: featureOverrides }));
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update tenant:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleFeatureOverride = (feature) => {
    setFeatureOverrides(prev =>
      prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]
    );
  };

  const handleOpenRoleEdit = async (user) => {
    setRoleEditUser(user);
    setRoleEditDialog(true);
    setRoleEditData(null);
    setRoleEditLoading(true);
    try {
      const res = await api.get(`/api/super-admin/tenants/${clientId}/users/${user.id}/access`);
      setRoleEditData(res.data);
    } catch {
      toast.error('Failed to load user access details');
    } finally {
      setRoleEditLoading(false);
    }
  };

  const handleSaveRole = async (newRole, newStatus) => {
    if (!roleEditUser) return;
    setRoleEditSaving(true);
    try {
      await api.put(`/api/super-admin/tenants/${clientId}/users/${roleEditUser.id}/access`, {
        role: newRole,
        roles: [newRole],
        status: newStatus,
      });
      toast.success(`Updated ${roleEditUser.full_name || roleEditUser.username} to ${newRole} (${newStatus})`);
      setRoleEditDialog(false);
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user access');
    } finally {
      setRoleEditSaving(false);
    }
  };


  const handleImpersonate = async () => {
    if (!selectedUser) return;
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/impersonate?user_id=${selectedUser.id}`);
      const redirectPath = res.data.redirect_path || '/dashboard';
      // Worker tokens land on /mobile/*, admin tokens on /dashboard.
      const url = `${window.location.origin}${redirectPath}?impersonate=${res.data.access_token}`;
      window.open(url, '_blank');
      setImpersonateDialog(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to impersonate:', err);
      alert('Failed to impersonate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePasswordReset = async () => {
    if (!passwordResetUser) return;
    setPasswordResetLoading(true);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/users/${passwordResetUser.id}/reset-password`);
      setTempPasswordResult({
        username: res.data.username,
        password: res.data.temp_password,
        fullName: res.data.user_full_name
      });
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleStripeSearch = async () => {
    if (!stripeSearchQuery.trim()) return;
    setStripeSearching(true);
    try {
      const res = await api.get(`/api/super-admin/stripe/customers/search?query=${encodeURIComponent(stripeSearchQuery)}`);
      setStripeSearchResults(res.data.customers || []);
    } catch (err) {
      console.error('Failed to search Stripe customers:', err);
      setStripeSearchResults([]);
    } finally {
      setStripeSearching(false);
    }
  };

  const handleLinkStripe = async () => {
    if (!stripeCustomerId.trim()) return;
    setStripeLinking(true);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/link-stripe`, {
        stripe_customer_id: stripeCustomerId
      });
      setTenant(prev => ({ ...prev, stripe_customer_id: stripeCustomerId }));
      setStripeLinkDialog(false);
      setStripeCustomerId('');
      setStripeSearchQuery('');
      setStripeSearchResults([]);
    } catch (err) {
      console.error('Failed to link Stripe customer:', err);
      alert(err.response?.data?.detail || 'Failed to link Stripe customer');
    } finally {
      setStripeLinking(false);
    }
  };

  const handleUnlinkStripe = async () => {
    setConfirmDialog({
      open: true,
      title: 'Unlink Stripe Customer',
      description: 'Are you sure you want to unlink this Stripe customer? The tenant will lose access to billing features.',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/api/super-admin/tenants/${clientId}/link-stripe`);
          setTenant(prev => ({ ...prev, stripe_customer_id: null }));
        } catch (err) {
          console.error('Failed to unlink Stripe customer:', err);
        }
      },
    });
  };

  const handlePriceLookup = async () => {
    const id = priceIdInput.trim();
    if (!id) return;
    if (!id.startsWith('price_')) { toast.error("Price ID must start with 'price_'"); return; }
    if (additionalProducts.some(p => p.price_id === id)) { toast.error('This price is already added'); return; }
    setPriceLookupLoading(true);
    try {
      const res = await api.get(`/api/super-admin/stripe/price-lookup?price_id=${encodeURIComponent(id)}`);
      if (!res.data.active) { toast.error('This price is not active in Stripe'); return; }
      setAdditionalProducts(prev => [...prev, res.data]);
      setPriceIdInput('');
      setCurrencyValidation(null);
      toast.success(`Added: ${res.data.product_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to look up price');
    } finally {
      setPriceLookupLoading(false);
    }
  };


  const handleValidateCheckoutCurrency = async () => {
    setCurrencyValidating(true);
    setCurrencyValidation(null);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/validate-checkout-currency`, {
        currency: inviteCurrency,
        additional_price_ids: additionalProducts,
      });
      setCurrencyValidation(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to validate prices');
    } finally {
      setCurrencyValidating(false);
    }
  };


  const handleSendSubscriptionInvite = async () => {
    setInviteLoading(true);
    try {
      const payload = {
        grace_days: 7,
        login_url: 'https://app.equiprcore.com/login',
        currency: inviteCurrency,
      };
      if (inviteNote.trim()) payload.note = inviteNote.trim();
      if (inviteCoupon.trim()) payload.stripe_coupon_id = inviteCoupon.trim();
      if (additionalProducts.length > 0) payload.additional_price_ids = additionalProducts;
      
      const res = await api.post(`/api/super-admin/tenants/${clientId}/send-subscription-invite`, payload);
      setTenant(prev => ({
        ...prev,
        subscription_status: 'grace_period',
        grace_period_expires_at: res.data.grace_period_expires_at
      }));
      toast.success(`Invite sent to ${res.data.admin_email}. Grace period: ${res.data.grace_days} days.${res.data.coupon_applied ? ' Coupon will be auto-applied at checkout.' : ''}`);
      setInviteDialogOpen(false);
      setInviteNote('');
      setInviteCoupon('');
      setAdditionalProducts([]);
      setPriceIdInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendUserInvite = async (user) => {
    setResendingInvite(user.id);
    try {
      await api.post(`/api/super-admin/tenants/${clientId}/users/${user.id}/resend-invite`);
      toast.success(`Invitation sent to ${user.email || user.username}`);
      // Refresh tenant data to update invite status
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleExtendGracePeriod = async () => {
    setExtendLoading(true);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/extend-grace-period`, {
        extra_days: extendDays
      });
      setTenant(prev => ({
        ...prev,
        subscription_status: res.data.subscription_status,
        grace_period_expires_at: res.data.grace_period_expires_at
      }));
      alert(`Grace period extended. New expiry: ${formatDate(res.data.grace_period_expires_at)}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to extend grace period');
    } finally {
      setExtendLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tenant not found</p>
        <Button variant="ghost" onClick={() => navigate('/super-admin/tenants')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/tenants')}>
          <ArrowLeft className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
        </Button>
        <div className="flex-1">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-3`}>
            <Building2 className="h-6 w-6 text-[#ffd108]" />
            {tenant.company_name}
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Client ID: {tenant.client_id}</p>
        </div>
        <div className="flex items-center gap-2">
          {!editMode ? (
            <Button onClick={() => setEditMode(true)} variant="outline">
              <Settings className="mr-2 h-4 w-4" /> Edit
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard title="Users" value={tenant.stats?.users?.length || 0} icon={Users} color="text-blue-400" isDark={isDark} />
        <StatCard title="Operators" value={tenant.stats?.operators_count || 0} icon={HardHat} color="text-green-400" isDark={isDark} />
        <StatCard title="Jobs" value={tenant.stats?.jobs_count || 0} icon={Briefcase} color="text-purple-400" isDark={isDark} />
        <StatCard title="Chat Rooms" value={tenant.stats?.chat_rooms || 0} icon={MessageSquare} color="text-cyan-400" isDark={isDark} />
        <StatCard title="Form Submissions" value={tenant.stats?.form_submissions || 0} icon={FileText} color="text-pink-400" isDark={isDark} />
      </div>

      {/* Recent Activity (last 30 days) */}
      <div>
        <p className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
          Recent Activity <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>(last 30 days · trend vs prior 30d)</span>
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="tenant-recent-activity">
          <StatCard title="Dockets Created" value={tenant.stats?.recent_activity?.dockets_created || 0} delta={tenant.stats?.recent_activity?.deltas?.dockets_created} icon={FileCheck} color="text-emerald-400" isDark={isDark} testId="ra-dockets-created" />
          <StatCard title="Invoices Exported" value={tenant.stats?.recent_activity?.invoices_exported || 0} delta={tenant.stats?.recent_activity?.deltas?.invoices_exported} icon={Receipt} color="text-amber-400" isDark={isDark} testId="ra-invoices-exported" />
          <StatCard title="Jobs Created" value={tenant.stats?.recent_activity?.jobs_created || 0} delta={tenant.stats?.recent_activity?.deltas?.jobs_created} icon={Briefcase} color="text-orange-400" isDark={isDark} testId="ra-jobs-created" />
          <StatCard title="Quotes Sent" value={tenant.stats?.recent_activity?.quotes_sent || 0} delta={tenant.stats?.recent_activity?.deltas?.quotes_sent} icon={FileSignature} color="text-indigo-400" isDark={isDark} testId="ra-quotes-sent" />
          <StatCard title="Forms Submitted" value={tenant.stats?.recent_activity?.forms_submitted || 0} delta={tenant.stats?.recent_activity?.deltas?.forms_submitted} icon={ClipboardList} color="text-pink-400" isDark={isDark} testId="ra-forms-submitted" />
          <StatCard title="Active Field Workers" value={tenant.stats?.recent_activity?.active_field_workers || 0} icon={HardHat} color="text-green-400" isDark={isDark} testId="ra-active-workers" />
          <StatCard title="Last Activity" value={formatRelativeTime(tenant.stats?.recent_activity?.last_activity_at)} valueColor={lastActivityColor(tenant.stats?.recent_activity?.last_activity_at)} icon={Activity} color="text-cyan-400" isDark={isDark} testId="ra-last-activity" />
        </div>
      </div>

      {/* Configuration */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tier</Label>
              {editMode ? (
                <Select value={editData.tier} onValueChange={(v) => setEditData(prev => ({ ...prev, tier: v }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div>
                  <Badge className="bg-[#ffd108] text-black">{tenant.tier || 'basic'}</Badge>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</Label>
              {editMode ? (
                <Select value={editData.subscription_status} onValueChange={(v) => setEditData(prev => ({ ...prev, subscription_status: v }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="onboarding">On-Boarding</SelectItem>
                    <SelectItem value="grace_period">Grace Period</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="trialing">Trialing</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div>
                  <Badge className={
                    tenant.subscription_status === 'suspended' ? (isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700') :
                    tenant.subscription_status === 'grace_period' ? (isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700') :
                    tenant.subscription_status === 'past_due' ? (isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700') :
                    tenant.subscription_status === 'onboarding' ? (isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700') :
                    tenant.subscription_status === 'trialing' ? (isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700') :
                    tenant.subscription_status === 'cancelled' ? (isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-600') :
                    isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                  }>{tenant.subscription_status === 'onboarding' ? 'On-Boarding' : (tenant.subscription_status || 'active')}</Badge>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Extra Operators</Label>
              {editMode ? (
                <Input
                  type="number"
                  value={editData.extra_operators}
                  onChange={(e) => setEditData(prev => ({ ...prev, extra_operators: parseInt(e.target.value) || 0 }))}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
              ) : (
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.extra_operators || 0}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Extra Users</Label>
              {editMode ? (
                <Input
                  type="number"
                  value={editData.extra_users}
                  onChange={(e) => setEditData(prev => ({ ...prev, extra_users: parseInt(e.target.value) || 0 }))}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
              ) : (
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.extra_users || 0}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Timesheet Licences</Label>
              {editMode ? (
                <Input
                  type="number"
                  value={editData.extra_operators_timesheet}
                  onChange={(e) => setEditData(prev => ({ ...prev, extra_operators_timesheet: parseInt(e.target.value) || 0 }))}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                  data-testid="tenant-extra-operators-timesheet-input"
                />
              ) : (
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="tenant-extra-operators-timesheet">{tenant.extra_operators_timesheet || 0}</p>
              )}
            </div>
          </div>
          {/* Monthly Recurring Revenue */}
          {showMRR && (
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`} data-testid="tenant-mrr-section">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Monthly Recurring Revenue</Label>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="tenant-mrr-value">
                    {(() => {
                      const amt = tenant.mrr;
                      if (amt === null || amt === undefined) return '—';
                      try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: tenant.mrr_currency || 'AUD', maximumFractionDigits: 0 }).format(amt); }
                      catch { return `$${Math.round(amt).toLocaleString()}`; }
                    })()}
                    <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>/mo</span>
                  </span>
                  {tenant.mrr_source === 'estimated' && <Badge className="bg-amber-500/20 text-amber-500">Estimated</Badge>}
                  {tenant.mrr_source === 'calculated' && <Badge className={isDark ? 'bg-slate-600/40 text-slate-300' : 'bg-gray-100 text-gray-600'}>Calculated from plan</Badge>}
                </div>
                {tenant.calculated_mrr !== null && tenant.calculated_mrr !== undefined && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Plan value: {(() => { try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: tenant.mrr_currency || 'AUD', maximumFractionDigits: 0 }).format(tenant.calculated_mrr); } catch { return `$${Math.round(tenant.calculated_mrr)}`; } })()}/mo
                    {' '}({tenant.tier || 'basic'} + {tenant.extra_operators || 0} op / {tenant.extra_users || 0} users)
                  </p>
                )}
              </div>
              {editMode && (
                <div className="space-y-1 sm:w-56">
                  <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Estimated MRR override</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 1500 (leave blank to auto-calc)"
                    value={editData.estimated_mrr}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_mrr: e.target.value }))}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                    data-testid="tenant-estimated-mrr-input"
                  />
                  <p className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Overrides the calculated value. Handy for tenants not linked to Stripe or on custom deals. Clear to revert to plan value.</p>
                </div>
              )}
            </div>
          </div>
          )}
          {/* Early Adopter Program */}
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className={`h-4 w-4 ${tenant.early_adopter ? 'text-[#ffd108] fill-[#ffd108]' : isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Early Adopter Program</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Enables feedback button and EA badge for this tenant</p>
                </div>
              </div>
              {editMode ? (
                <button
                  type="button"
                  data-testid="early-adopter-toggle"
                  onClick={() => setEditData(prev => ({ ...prev, early_adopter: !prev.early_adopter }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editData.early_adopter ? 'bg-[#ffd108]' : isDark ? 'bg-slate-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editData.early_adopter ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              ) : (
                <Badge
                  data-testid="early-adopter-status-badge"
                  className={tenant.early_adopter
                    ? 'bg-[#ffd108]/20 text-[#ffd108] border border-[#ffd108]/30'
                    : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}
                >
                  {tenant.early_adopter ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </div>
          </div>
          {/* Phone Support */}
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`} data-testid="phone-support-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone className={`h-4 w-4 ${tenant.phone_support ? 'text-emerald-500' : isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Phone Support</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Marks this tenant as entitled to phone support (shows a phone icon on the Tenants list)</p>
                </div>
              </div>
              {editMode ? (
                <button
                  type="button"
                  data-testid="phone-support-toggle"
                  onClick={() => setEditData(prev => ({ ...prev, phone_support: !prev.phone_support }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editData.phone_support ? 'bg-emerald-500' : isDark ? 'bg-slate-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editData.phone_support ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              ) : (
                <Badge
                  data-testid="phone-support-status-badge"
                  className={tenant.phone_support
                    ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30'
                    : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}
                >
                  {tenant.phone_support ? 'Enabled' : 'Disabled'}
                </Badge>
              )}
            </div>
          </div>
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              Created: {tenant.created_at ? format(new Date(tenant.created_at), 'PPpp') : 'N/A'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Feature Overrides */}
      {editMode && (
        <FeatureOverridesCard
          tier={editData.tier || tenant?.tier || 'basic'}
          overrides={featureOverrides}
          onToggle={toggleFeatureOverride}
          isDark={isDark}
        />
      )}
      {!editMode && featureOverrides.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader className="py-3">
            <CardTitle className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <Star className="h-4 w-4 text-[#ffd108]" />
              Feature Overrides
              <Badge variant="secondary" className="text-xs ml-auto">{featureOverrides.length} active</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-1">
              {featureOverrides.map(f => (
                <Badge key={f} className="bg-[#ffd108]/10 text-[#ffd108] border border-[#ffd108]/30 text-xs">
                  {f.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Management */}
      {isAdmin && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <Send className="h-5 w-5 text-[#ffd108]" />
              Subscription Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Show grace period info if active */}
              {(tenant.subscription_status === 'grace_period' || tenant.subscription_status === 'suspended') && (
                <div className={`rounded-lg p-4 ${
                  tenant.subscription_status === 'suspended' 
                    ? 'bg-red-500/10 border border-red-500/30' 
                    : 'bg-amber-500/10 border border-amber-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {tenant.subscription_status === 'suspended' ? (
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                    ) : (
                      <Clock className="h-4 w-4 text-amber-400" />
                    )}
                    <span className={`font-medium ${
                      tenant.subscription_status === 'suspended' ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      {tenant.subscription_status === 'suspended' ? 'Account Suspended' : 'Grace Period Active'}
                    </span>
                  </div>
                  {tenant.grace_period_expires_at && (
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {tenant.subscription_status === 'suspended' 
                        ? `Grace period expired: ${format(new Date(tenant.grace_period_expires_at), 'PPp')}`
                        : `Expires: ${format(new Date(tenant.grace_period_expires_at), 'PPp')}`
                      }
                    </p>
                  )}
                  {tenant.subscription_invite_sent_at && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      Invite sent: {format(new Date(tenant.subscription_invite_sent_at), 'PPp')}
                    </p>
                  )}
                  
                  {/* Extend Grace Period */}
                  <div className="flex items-center gap-2 mt-3">
                    <Input
                      type="number"
                      value={extendDays}
                      onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                      className={`w-20 h-8 text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`}
                      min={1}
                      max={90}
                    />
                    <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>days</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleExtendGracePeriod}
                      disabled={extendLoading}
                      className={`h-8 ${isDark ? 'border-slate-600 text-slate-300 hover:text-white' : ''}`}
                      data-testid="extend-grace-period-btn"
                    >
                      {extendLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                      Extend Grace Period
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Mismatch warning: DB says active but Stripe has no active sub */}
              {stripeSummary?.status_check?.mismatch && (
                <div className={`rounded-lg p-3 flex items-start gap-2 ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`} data-testid="subscription-mismatch-warning">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                      Subscription Mismatch Detected
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
                      Database status is <span className="font-semibold">{stripeSummary.core_status}</span> but Stripe shows{' '}
                      {stripeSummary.status_check.stripe_statuses?.length > 0
                        ? stripeSummary.status_check.stripe_statuses.join(', ')
                        : 'no active subscriptions'}.
                      Send a subscription invite to resolve this.
                    </p>
                  </div>
                </div>
              )}

              {/* Send Subscription Invite - show when no customer, not active, or mismatch detected */}
              {(!tenant.stripe_customer_id || !['active'].includes(tenant.subscription_status) || stripeSummary?.status_check?.mismatch) && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => { setInviteNote(''); setInviteCoupon(''); setInviteCurrency(tenant?.currency || 'aud'); setAdditionalProducts([]); setPriceIdInput(''); setCurrencyValidation(null); setInviteDialogOpen(true); }}
                    disabled={inviteLoading}
                    className="bg-[#ffd108] hover:bg-[#e6bc00] text-black font-medium"
                    data-testid="send-subscription-invite-btn"
                  >
                    {inviteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    {tenant.subscription_status === 'grace_period' ? 'Resend Subscription Invite' : 'Send Subscription Invite'}
                  </Button>
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Sends email to tenant admin with 7-day grace period
                  </span>
                </div>
              )}
              
              {tenant.subscription_status === 'active' && tenant.stripe_customer_id && !stripeSummary?.status_check?.mismatch && (
                <p className={`text-sm flex items-center gap-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  <Check className="h-4 w-4" />
                  Tenant has an active subscription
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stripe Integration - Admin only */}
      {isAdmin && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <CreditCard className="h-5 w-5 text-[#ffd108]" />
              Stripe Integration
            </CardTitle>
            {tenant.stripe_customer_id ? (
              <Button size="sm" variant="outline" onClick={handleUnlinkStripe} className="text-red-500 hover:text-red-400">
                <Unlink className="mr-2 h-4 w-4" /> Unlink
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setStripeLinkDialog(true)}>
                <Link className="mr-2 h-4 w-4" /> Link Customer
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {tenant.stripe_customer_id ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className={isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}>Linked</Badge>
                  <span className={`font-mono text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {tenant.stripe_customer_id}
                  </span>
                </div>
                <a 
                  href={`https://dashboard.stripe.com/customers/${tenant.stripe_customer_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  View in Stripe Dashboard <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No Stripe customer linked</p>
                <p className="text-sm mt-1">Link a Stripe customer to enable billing features</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Live Stripe Billing Summary - Admin only, shown when Stripe is linked */}
      {isAdmin && tenant.stripe_customer_id && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'} data-testid="sa-stripe-summary-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <Receipt className="h-5 w-5 text-[#ffd108]" />
              Live Stripe Billing
            </CardTitle>
            <Button size="sm" variant="outline" onClick={fetchStripeSummary} disabled={stripeSummaryLoading}>
              {stripeSummaryLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </CardHeader>
          <CardContent>
            {stripeSummaryLoading && !stripeSummary ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : stripeSummary?.has_stripe === false ? (
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No Stripe data available</p>
            ) : stripeSummary ? (
              <div className="space-y-4">
                {/* Status Mismatch Warning */}
                {stripeSummary.status_check?.mismatch && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2" data-testid="sa-billing-mismatch-warning">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-500">Status Mismatch</p>
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                        Core says <Badge variant="outline" className="mx-1">{stripeSummary.core_status}</Badge> but Stripe shows
                        <Badge variant="outline" className="ml-1">
                          {stripeSummary.status_check.stripe_statuses.length > 0 ? stripeSummary.status_check.stripe_statuses.join(', ') : 'no active subs'}
                        </Badge>
                      </p>
                    </div>
                  </div>
                )}

                {/* Customer Info */}
                {stripeSummary.customer && (
                  <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Customer</p>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{stripeSummary.customer.name || '—'}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email</p>
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{stripeSummary.customer.email || '—'}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Delinquent</p>
                      <Badge variant={stripeSummary.customer.delinquent ? 'destructive' : 'secondary'}>
                        {stripeSummary.customer.delinquent ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Balance</p>
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>${stripeSummary.customer.balance?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                )}

                {/* Customer Discount */}
                {stripeSummary.customer_discount && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="sa-billing-customer-discount">
                    <p className="text-xs font-medium mb-1 text-green-600">Customer Discount</p>
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">
                      {stripeSummary.customer_discount.coupon_name || stripeSummary.customer_discount.coupon_id}
                      {stripeSummary.customer_discount.percent_off ? ` — ${stripeSummary.customer_discount.percent_off}% off` : ''}
                      {stripeSummary.customer_discount.amount_off ? ` — $${stripeSummary.customer_discount.amount_off} off` : ''}
                      {stripeSummary.customer_discount.duration === 'forever' ? ' (forever)' : stripeSummary.customer_discount.duration === 'repeating' ? ` (${stripeSummary.customer_discount.duration_in_months || '?'} months)` : stripeSummary.customer_discount.duration === 'once' ? ' (one-time)' : ''}
                    </p>
                    {stripeSummary.customer_discount.discount_end && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Expires {new Date(stripeSummary.customer_discount.discount_end * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Subscriptions */}
                <div data-testid="sa-billing-subscriptions">
                  <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Subscriptions ({stripeSummary.subscriptions?.length || 0})
                  </p>
                  {(stripeSummary.subscriptions || []).length === 0 ? (
                    <p className="text-sm text-amber-500 flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" /> No subscriptions found in Stripe
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {stripeSummary.subscriptions.map((sub) => (
                        <div key={sub.id} className={`p-3 rounded-lg border ${isDark ? 'border-slate-600 bg-slate-700/30' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant={sub.status === 'active' ? 'default' : sub.status === 'trialing' ? 'secondary' : 'destructive'}
                                     className={sub.status === 'active' ? 'bg-green-500' : ''}>
                                {sub.status}
                              </Badge>
                              {sub.cancel_at_period_end && <Badge variant="destructive" className="text-xs">Cancels at end</Badge>}
                            </div>
                            <span className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{sub.id}</span>
                          </div>
                          {/* Subscription discount */}
                          {sub.discount && (
                            <div className="mb-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-xs text-green-600">
                              Discount: {sub.discount.coupon_name || sub.discount.coupon_id}
                              {sub.discount.percent_off ? ` (${sub.discount.percent_off}% off)` : sub.discount.amount_off ? ` ($${sub.discount.amount_off} off)` : ''}
                            </div>
                          )}
                          {/* Items */}
                          {(sub.items || []).map((item, i) => (
                            <div key={i} className={`flex items-center justify-between text-sm py-1 ${i > 0 ? 'border-t ' + (isDark ? 'border-slate-600' : 'border-gray-200') : ''}`}>
                              <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                                {item.product_name || item.price_id}
                                {item.quantity > 1 && <span className="text-muted-foreground ml-1">×{item.quantity}</span>}
                              </span>
                              <span className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                ${item.amount?.toFixed(2)}/{item.interval}
                                {item.quantity > 1 && ` ($${(item.amount * item.quantity)?.toFixed(2)})`}
                              </span>
                            </div>
                          ))}
                          {sub.current_period_end && (
                            <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                              Renews: {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Users */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Users</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPasswordResetDialog(true)} data-testid="reset-password-btn">
              <KeyRound className="mr-2 h-4 w-4" /> Reset Password
            </Button>
            <Button size="sm" variant="outline" onClick={() => setImpersonateDialog(true)} data-testid="impersonate-btn">
              <UserCog className="mr-2 h-4 w-4" /> Impersonate
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-slate-700' : 'border-gray-200'}>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Name</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Username</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Role</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Source</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Invite Status</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Last Active</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenant.stats?.users?.map((user) => (
                <TableRow key={user.id} className={isDark ? 'border-slate-700' : 'border-gray-100'} data-testid={`tenant-user-row-${user.id}`}>
                  <TableCell className={isDark ? 'text-white' : 'text-gray-900'}>{user.full_name}</TableCell>
                  <TableCell className={isDark ? 'text-slate-400' : 'text-gray-500'}>{user.username}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{user.role}</Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const src = user.source || 'legacy';
                      const cls = src === 'unified'
                        ? 'border-emerald-500 text-emerald-400'
                        : src === 'both'
                          ? 'border-amber-500 text-amber-400'
                          : 'border-slate-500 text-slate-400';
                      return (
                        <Badge variant="outline" className={`text-xs capitalize ${cls}`} data-testid={`user-source-${user.id}`}>
                          {src}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // Determine invite status
                      if (user.last_login || user.invite_accepted || user.password_hash_set) {
                        return (
                          <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-400" data-testid={`invite-status-${user.id}`}>
                            <MailCheck className="h-3 w-3 mr-1" /> Activated
                          </Badge>
                        );
                      }
                      if (user.invite_sent) {
                        return (
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="text-xs border-amber-500 text-amber-400" data-testid={`invite-status-${user.id}`}>
                              <Mail className="h-3 w-3 mr-1" /> Sent
                            </Badge>
                            {user.invite_sent_at && (
                              <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                {formatDistanceToNow(new Date(user.invite_sent_at), { addSuffix: true })}
                              </span>
                            )}
                          </div>
                        );
                      }
                      return (
                        <Badge variant="outline" className="text-xs border-red-500 text-red-400" data-testid={`invite-status-${user.id}`}>
                          <Clock className="h-3 w-3 mr-1" /> Not Sent
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {user.created_at ? formatDistanceToNow(new Date(user.created_at), { addSuffix: true }) : 'N/A'}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {user.last_login ? formatDistanceToNow(new Date(user.last_login), { addSuffix: true }) : 'Never'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {/* Show resend invite button if user hasn't activated yet */}
                      {!user.last_login && !user.invite_accepted && !user.password_hash_set && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => handleResendUserInvite(user)}
                          disabled={resendingInvite === user.id}
                          data-testid={`resend-invite-${user.id}`}
                        >
                          {resendingInvite === user.id
                            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            : <Mail className="h-3 w-3 mr-1" />}
                          {user.invite_sent ? 'Resend' : 'Send Invite'}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleOpenRoleEdit(user)} data-testid={`edit-role-${user.id}`}>
                        <Settings className="h-3 w-3 mr-1" /> Edit Access
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Workers (mobile operators) */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <HardHat className="h-5 w-5 text-green-400" />
            Workers (Mobile Operators)
            <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              ({tenant.stats?.workers?.length || 0})
            </span>
          </CardTitle>
          {tenant.stats?.workers?.some(w => w.linkage_status === 'unlinked') && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
              onClick={async () => {
                try {
                  const res = await api.post(`/api/super-admin/tenants/${clientId}/repair-all-worker-links`);
                  const { repaired, skipped, errors } = res.data;
                  if (repaired > 0) {
                    alert(`Repaired ${repaired} link(s). Skipped: ${skipped}. Errors: ${errors.length}`);
                    fetchTenant();
                  } else {
                    alert('No links needed repair.');
                  }
                } catch (e) {
                  alert(e.response?.data?.detail || 'Failed to repair links');
                }
              }}
              data-testid="repair-all-links-btn"
            >
              <Link className="h-3 w-3 mr-1" /> Repair All Links
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {tenant.stats?.workers?.length ? (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-gray-200'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Name</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Code</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Email</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Mobile</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Linkage</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.stats.workers.map((w) => (
                  <TableRow key={w.id} className={isDark ? 'border-slate-700' : 'border-gray-100'} data-testid={`tenant-worker-row-${w.id}`}>
                    <TableCell className={isDark ? 'text-white' : 'text-gray-900'}>{w.name || '—'}</TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{w.operator_code || '—'}</TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{w.email || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        !w.mobile_enabled
                          ? 'border-slate-500 text-slate-500'
                          : (w.mobile_licence_type === 'timesheet'
                              ? 'border-blue-400 text-blue-400'
                              : 'border-green-500 text-green-500')
                      } data-testid={`worker-mobile-badge-${w.id}`}>
                        {!w.mobile_enabled ? 'Disabled' : (w.mobile_licence_type === 'timesheet' ? 'Timesheet' : 'Full')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {w.linkage_status === 'linked' && (
                        <Badge variant="outline" className="border-green-500 text-green-500" data-testid={`linkage-linked-${w.id}`}>
                          <Link className="h-3 w-3 mr-1" /> Linked
                        </Badge>
                      )}
                      {w.linkage_status === 'unlinked' && (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="border-amber-500 text-amber-500" data-testid={`linkage-unlinked-${w.id}`}>
                            <Unlink className="h-3 w-3 mr-1" /> Unlinked
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-amber-500 hover:text-amber-400"
                            onClick={async () => {
                              try {
                                await api.post(`/api/super-admin/tenants/${clientId}/repair-worker-link/${w.id}`);
                                fetchTenant();
                              } catch (e) {
                                alert(e.response?.data?.detail || 'Failed to repair link');
                              }
                            }}
                            data-testid={`repair-link-btn-${w.id}`}
                          >
                            Repair
                          </Button>
                        </div>
                      )}
                      {w.linkage_status === 'no_account' && (
                        <Badge variant="outline" className="border-red-500 text-red-500" data-testid={`linkage-no-account-${w.id}`}>
                          No Account
                        </Badge>
                      )}
                      {w.linkage_status === 'not_applicable' && (
                        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser({ id: w.id, full_name: w.name, username: w.operator_code || w.email, role: 'operator', _isWorker: true });
                          setImpersonateDialog(true);
                        }}
                        data-testid={`impersonate-worker-btn-${w.id}`}
                      >
                        <UserCog className="mr-1 h-3 w-3" /> Impersonate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className={`text-sm py-6 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              No workers configured for this tenant.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Demo Data Management */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'} data-testid="demo-data-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Package className="h-5 w-5 text-purple-400" />
            Demo Data
            {demoDataStatus?.has_demo_data && (
              <Badge variant="outline" className="ml-2 border-purple-500 text-purple-400">
                {demoDataStatus.total} records
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {demoDataStatus?.has_demo_data ? (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 ${isDark ? 'bg-purple-900/20 border border-purple-800/40' : 'bg-purple-50 border border-purple-200'}`}>
                <p className={`text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                  Demo data is active
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Seeded {demoDataStatus.seeded_at ? formatDistanceToNow(new Date(demoDataStatus.seeded_at), { addSuffix: true }) : 'recently'}
                  {demoDataStatus.seed_file && ` from ${demoDataStatus.seed_file}`}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(demoDataStatus.counts || {}).map(([key, count]) => (
                    <Badge key={key} variant="secondary" className={isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-700'}>
                      {key}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
              <ConfirmationDialog
                open={demoPurgeConfirm}
                onOpenChange={setDemoPurgeConfirm}
                title="Purge Demo Data?"
                description={`This will permanently remove ${demoDataStatus?.total || 0} demo records from this tenant. Real data added by the client will NOT be affected.`}
                confirmText="Purge"
                variant="destructive"
                onConfirm={() => { setDemoPurgeConfirm(false); handlePurgeDemoData(); }}
              />
              <Button
                variant="destructive"
                size="sm"
                disabled={demoPurging}
                onClick={() => setDemoPurgeConfirm(true)}
                data-testid="purge-demo-data-btn"
              >
                {demoPurging ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Purging...</> : <><Trash2 className="h-4 w-4 mr-1" /> Purge Demo Data</>}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                No demo data loaded. Seed this tenant with sample data so prospects can explore the system.
              </p>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Seed File</Label>
                  <Select value={seedFileSelect} onValueChange={setSeedFileSelect}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} data-testid="seed-file-select">
                      <SelectValue placeholder={tenant?.industry ? `Auto (${tenant.industry})` : 'Select seed file'} />
                    </SelectTrigger>
                    <SelectContent>
                      {seedFiles.map(sf => (
                        <SelectItem key={sf.filename} value={sf.filename}>
                          {sf.industry || sf.filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleSeedDemoData}
                  disabled={demoSeeding}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="seed-demo-data-btn"
                >
                  {demoSeeding ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Seeding...</> : <><Plus className="h-4 w-4 mr-1" /> Seed Demo Data</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Tickets */}
      {tenant.stats?.recent_tickets?.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Ticket className="h-5 w-5 text-orange-400" />
              Recent Tickets
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-gray-200'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Subject</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.stats.recent_tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className={`cursor-pointer ${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'}`}
                    onClick={() => navigate(`/super-admin/tickets/${ticket.id}`)}
                  >
                    <TableCell className="text-[#ffd108] font-mono text-sm">{ticket.ticket_number}</TableCell>
                    <TableCell className={isDark ? 'text-white' : 'text-gray-900'}>{ticket.subject}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{ticket.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ticket.priority === 'urgent' ? 'destructive' : 'secondary'} className="capitalize">
                        {ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Impersonate Dialog */}

      {/* ── Danger Zone ─────────────────────────────── */}
      <Card className="border-red-500/40 mt-6" data-testid="danger-zone-card">
        <CardHeader className="pb-2">
          <CardTitle className={`text-lg flex items-center gap-2 text-red-500`}>
            <AlertTriangle className="h-5 w-5" /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Permanently delete this tenant</p>
              <p className="text-sm text-muted-foreground">
                Remove <strong>{tenant?.client_id}</strong> and all associated data. This action is irreversible.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => { setPermDeleteOpen(true); setPermDeleteConfirmText(''); }}
              data-testid="permanent-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete Tenant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={permDeleteOpen} onOpenChange={(open) => { setPermDeleteOpen(open); if (!open) setPermDeleteConfirmText(''); }}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Permanently Delete Tenant
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{tenant?.company_name}</strong> ({tenant?.client_id}) and
              all of its data — users, jobs, dockets, equipment, forms, invoices, everything.
              <br /><strong>This cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>
              Type <strong>{tenant?.client_id}</strong> to confirm
            </Label>
            <Input
              value={permDeleteConfirmText}
              onChange={(e) => setPermDeleteConfirmText(e.target.value)}
              placeholder={tenant?.client_id}
              data-testid="permanent-delete-confirm-input"
              className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDeleteOpen(false)} disabled={permDeleting}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={permDeleteConfirmText !== tenant?.client_id || permDeleting}
              data-testid="permanent-delete-confirm-btn"
              onClick={async () => {
                setPermDeleting(true);
                try {
                  const res = await api.delete(`/api/super-admin/tenants/${tenant.client_id}/permanent`);
                  toast.success(`Tenant ${tenant.client_id} permanently deleted (${res.data.total_deleted} records removed)`);
                  setPermDeleteOpen(false);
                  navigate('/super-admin/tenants');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to delete tenant');
                } finally {
                  setPermDeleting(false);
                }
              }}
            >
              {permDeleting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting...</> : 'I understand, delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Dialog — kept below */}
      <Dialog open={impersonateDialog} onOpenChange={(open) => {
        setImpersonateDialog(open);
        if (!open) setSelectedUser(null);
      }}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Impersonate User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Select a user to impersonate. This will open a new tab logged in as that user.
              Tenant users open the desktop dashboard; workers open the mobile portal.
            </p>

            {tenant.stats?.users?.length > 0 && (
              <div className="space-y-2">
                <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Tenant Users</p>
                {tenant.stats.users.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedUser({ ...user, _isWorker: false })}
                    data-testid={`impersonate-user-option-${user.id}`}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedUser?.id === user.id && !selectedUser?._isWorker
                        ? 'bg-[#ffd108]/20 border border-[#ffd108]'
                        : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.username} • {user.role}</p>
                  </div>
                ))}
              </div>
            )}

            {tenant.stats?.workers?.length > 0 && (
              <div className="space-y-2">
                <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Workers (Mobile)</p>
                {tenant.stats.workers.map((w) => (
                  <div
                    key={w.id}
                    onClick={() => setSelectedUser({ id: w.id, full_name: w.name, username: w.operator_code || w.email, role: 'operator', _isWorker: true })}
                    data-testid={`impersonate-worker-option-${w.id}`}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedUser?.id === w.id && selectedUser?._isWorker
                        ? 'bg-green-500/20 border border-green-500'
                        : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                      <HardHat className="h-3 w-3 text-green-400" />
                      {w.name || '—'}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {w.operator_code || w.email || '—'} • worker
                      {!w.mobile_enabled && <span className="ml-1 text-amber-400">(mobile disabled)</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImpersonateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleImpersonate}
              disabled={!selectedUser}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              data-testid="impersonate-confirm-btn"
            >
              <ExternalLink className="mr-2 h-4 w-4" /> Open as User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialog} onOpenChange={(open) => {
        setPasswordResetDialog(open);
        if (!open) {
          setPasswordResetUser(null);
          setTempPasswordResult(null);
        }
      }}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-md`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              <KeyRound className="inline mr-2 h-5 w-5 text-amber-500" />
              Reset User Password
            </DialogTitle>
          </DialogHeader>
          
          {!tempPasswordResult ? (
            <>
              <div className="space-y-4 py-4">
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Select a user to reset their password. A temporary password will be generated.
                </p>
                <div className="space-y-2">
                  {/* Office users */}
                  {tenant?.stats?.users?.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => setPasswordResetUser(user)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        passwordResetUser?.id === user.id
                          ? 'bg-amber-500/20 border border-amber-500'
                          : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.username} • {user.role}</p>
                    </div>
                  ))}
                  {/* Workers with unified accounts (operator-only users) */}
                  {tenant?.stats?.workers?.filter(w => w.unified_user_id && w.email && !tenant?.stats?.users?.some(u => u.email === w.email)).map((worker) => (
                    <div
                      key={worker.unified_user_id}
                      onClick={() => setPasswordResetUser({ id: worker.unified_user_id, full_name: worker.name, username: worker.email, role: 'operator' })}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        passwordResetUser?.id === worker.unified_user_id
                          ? 'bg-amber-500/20 border border-amber-500'
                          : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{worker.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{worker.email} • operator</p>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPasswordResetDialog(false)}>Cancel</Button>
                <Button
                  onClick={handlePasswordReset}
                  disabled={!passwordResetUser || passwordResetLoading}
                  className="bg-amber-500 text-black hover:bg-amber-600"
                >
                  {passwordResetLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                  ) : (
                    <><KeyRound className="mr-2 h-4 w-4" /> Reset Password</>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                    Password reset successfully!
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-green-500' : 'text-green-600'}`}>
                    The user will be required to change this password on next login.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Username</p>
                    <p className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>{tempPasswordResult.username}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Temporary Password</p>
                    <div className="flex items-center gap-2">
                      <code className={`flex-1 p-2 rounded font-mono text-lg ${isDark ? 'bg-slate-700 text-amber-400' : 'bg-gray-100 text-amber-600'}`}>
                        {tempPasswordResult.password}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(tempPasswordResult.password)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setPasswordResetDialog(false);
                  setPasswordResetUser(null);
                  setTempPasswordResult(null);
                }} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Stripe Link Dialog - Admin only */}
      {isAdmin && (
        <Dialog open={stripeLinkDialog} onOpenChange={setStripeLinkDialog}>
          <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-lg`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Link Stripe Customer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Search */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Search by email or name</Label>
                <div className="flex gap-2">
                  <Input
                    value={stripeSearchQuery}
                    onChange={(e) => setStripeSearchQuery(e.target.value)}
                    placeholder="customer@example.com"
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                    onKeyDown={(e) => e.key === 'Enter' && handleStripeSearch()}
                  />
                  <Button onClick={handleStripeSearch} disabled={stripeSearching} variant="outline">
                    {stripeSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {stripeSearchResults.length > 0 && (
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Search Results</Label>
                  <div className={`border rounded-md max-h-48 overflow-y-auto ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                    {stripeSearchResults.map((customer) => (
                      <div
                        key={customer.id}
                        className={`p-3 cursor-pointer flex justify-between items-center ${
                          stripeCustomerId === customer.id 
                            ? (isDark ? 'bg-slate-700' : 'bg-blue-50') 
                            : (isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50')
                        } ${isDark ? 'border-b border-slate-600 last:border-0' : 'border-b border-gray-100 last:border-0'}`}
                        onClick={() => setStripeCustomerId(customer.id)}
                      >
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{customer.name || 'No name'}</p>
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{customer.email}</p>
                        </div>
                        <code className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{customer.id}</code>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Entry */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Or enter Customer ID directly</Label>
                <Input
                  value={stripeCustomerId}
                  onChange={(e) => setStripeCustomerId(e.target.value)}
                  placeholder="cus_xxxxxxxxxxxx"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white font-mono' : 'bg-gray-50 border-gray-200 text-gray-900 font-mono'}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => {
                setStripeLinkDialog(false);
                setStripeCustomerId('');
                setStripeSearchQuery('');
                setStripeSearchResults([]);
              }}>Cancel</Button>
              <Button
                onClick={handleLinkStripe}
                disabled={!stripeCustomerId || stripeLinking}
                className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              >
                {stripeLinking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link className="mr-2 h-4 w-4" />}
                Link Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Role / Access Edit Dialog */}
      <Dialog open={roleEditDialog} onOpenChange={(open) => {
        setRoleEditDialog(open);
        if (!open) { setRoleEditUser(null); setRoleEditData(null); }
      }}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-lg`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              <Settings className="inline mr-2 h-5 w-5 text-[#ffd108]" />
              Edit User Access
            </DialogTitle>
          </DialogHeader>
          {roleEditLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" /></div>
          ) : roleEditData ? (() => {
            const ta = roleEditData.unified?.tenant_access;
            const legacy = roleEditData.legacy;
            const currentRole = ta?.role || ta?.roles?.[0] || legacy?.role || 'unknown';
            const currentStatus = ta?.status || 'missing';
            const currentRoles = ta?.roles || (ta?.role ? [ta.role] : []);
            const hasOperatorId = !!ta?.operator_id;
            const mobileEnabled = !!ta?.mobile_enabled;
            return (
              <div className="space-y-4 py-2">
                <div className={`p-3 rounded-lg text-sm ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                  <p className={isDark ? 'text-white' : 'text-gray-900'}><strong>{roleEditUser?.full_name || roleEditUser?.username}</strong></p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{roleEditData.unified?.email || legacy?.username || '—'}</p>
                </div>

                {/* Diagnostic info */}
                <div className={`p-3 rounded-lg space-y-1 text-xs ${isDark ? 'bg-slate-900/50 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                  <p><strong>Unified ID:</strong> {roleEditData.unified?.id || 'None'}</p>
                  <p><strong>Legacy ID:</strong> {legacy?.id || 'None'} {legacy?.role ? `(role: ${legacy.role})` : ''}</p>
                  <p><strong>tenant_access.role:</strong> {ta?.role || 'missing'}</p>
                  <p><strong>tenant_access.roles:</strong> {currentRoles.length > 0 ? currentRoles.join(', ') : 'missing'}</p>
                  <p><strong>tenant_access.status:</strong> <span className={currentStatus === 'active' ? 'text-green-400' : currentStatus === 'missing' ? 'text-red-400' : 'text-amber-400'}>{currentStatus}</span></p>
                  {hasOperatorId && <p><strong>operator_id:</strong> {ta.operator_id}</p>}
                  {mobileEnabled && <p><strong>mobile_enabled:</strong> true</p>}
                </div>

                {/* Role selector */}
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Role</Label>
                  <Select defaultValue={currentRole} onValueChange={(v) => setRoleEditUser(prev => ({ ...prev, _newRole: v }))}>
                    <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="dispatcher">Dispatcher</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="compliance_officer">Compliance</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status selector */}
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Status</Label>
                  <Select defaultValue={currentStatus === 'missing' ? 'active' : currentStatus} onValueChange={(v) => setRoleEditUser(prev => ({ ...prev, _newStatus: v }))}>
                    <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {currentStatus === 'missing' && (
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                    <AlertTriangle className="inline h-3 w-3 mr-1" />
                    This user has no status field — they cannot log in. Setting status to "active" will restore access.
                  </div>
                )}
              </div>
            );
          })() : (
            <p className={`py-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>No access data found for this user.</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRoleEditDialog(false)}>Cancel</Button>
            <Button
              onClick={() => {
                const ta = roleEditData?.unified?.tenant_access;
                const legacy = roleEditData?.legacy;
                const fallbackRole = ta?.role || legacy?.role || 'admin';
                const fallbackStatus = ta?.status || 'active';
                handleSaveRole(
                  roleEditUser?._newRole || fallbackRole,
                  roleEditUser?._newStatus || (ta?.status ? fallbackStatus : 'active')
                );
              }}
              disabled={roleEditSaving || roleEditLoading || !roleEditData}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              data-testid="save-role-btn"
            >
              {roleEditSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{tenant?.subscription_status === 'grace_period' ? 'Resend' : 'Send'} Subscription Invite</DialogTitle>
            <DialogDescription>
              Send an invite email to the tenant admin. This starts a 7-day grace period.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                value={inviteNote}
                onChange={e => setInviteNote(e.target.value)}
                placeholder="Add a personal message included in the invite email..."
                rows={3}
                data-testid="invite-note-textarea"
              />
              <p className="text-xs text-muted-foreground">This message will appear in the invite email sent to the tenant admin.</p>
            </div>
            <div className="space-y-2">
              <Label>Stripe Coupon Code (optional)</Label>
              <Input
                value={inviteCoupon}
                onChange={e => setInviteCoupon(e.target.value)}
                placeholder="e.g. WELCOME20"
                data-testid="invite-coupon-input"
              />
              <p className="text-xs text-muted-foreground">Pre-created Stripe coupon ID. Auto-applied at checkout — the tenant won't need to enter it.</p>
            </div>
            {/* Billing Currency */}
            <div className="space-y-2">
              <Label>Billing Currency</Label>
              <select
                value={inviteCurrency}
                onChange={e => { setInviteCurrency(e.target.value); setCurrencyValidation(null); }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="invite-currency-select"
              >
                <option value="aud">AUD — Australian Dollar</option>
                <option value="gbp">GBP — British Pound</option>
                <option value="usd">USD — US Dollar</option>
                <option value="nzd">NZD — New Zealand Dollar</option>
                <option value="eur">EUR — Euro</option>
                <option value="cad">CAD — Canadian Dollar</option>
              </select>
              <p className="text-xs text-muted-foreground">Currency used for the Stripe checkout session. Must match a multi-currency variant on the Price.</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleValidateCheckoutCurrency}
                disabled={currencyValidating}
                className="w-full"
                data-testid="invite-validate-currency-btn"
              >
                {currencyValidating ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Checking prices...</> : <><Search className="h-3.5 w-3.5 mr-2" /> Validate Prices for {inviteCurrency.toUpperCase()}</>}
              </Button>
              {currencyValidation && (
                <div className={`rounded-lg border p-3 space-y-2 ${currencyValidation.all_supported ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5'}`} data-testid="currency-validation-results">
                  <p className={`text-xs font-medium flex items-center gap-1.5 ${currencyValidation.all_supported ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {currencyValidation.all_supported ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                    {currencyValidation.message}
                  </p>
                  {currencyValidation.results?.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs" data-testid={`validation-result-${idx}`}>
                      {r.supported
                        ? <Check className="h-3 w-3 text-green-500 shrink-0" />
                        : <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />}
                      <span className={r.supported ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400 font-medium'}>{r.label}</span>
                      <span className="text-muted-foreground/50 font-mono text-[10px] ml-auto">{r.available_currencies?.join(', ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Additional Products */}
            <div className="space-y-2">
              <Label>Additional Products</Label>
              <div className="flex gap-2">
                <Input
                  value={priceIdInput}
                  onChange={e => setPriceIdInput(e.target.value)}
                  placeholder="price_xxxxxxxx"
                  className="flex-1 font-mono text-xs"
                  data-testid="invite-price-id-input"
                  onKeyDown={e => e.key === 'Enter' && handlePriceLookup()}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handlePriceLookup}
                  disabled={priceLookupLoading || !priceIdInput.trim()}
                  data-testid="invite-price-add-btn"
                >
                  {priceLookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Paste a Stripe Price ID and click + to look it up. These products will be added to the subscription.</p>
              {additionalProducts.length > 0 && (
                <div className="space-y-1.5 mt-2" data-testid="invite-additional-products-list">
                  {additionalProducts.map((p, idx) => (
                    <div key={p.price_id} className="flex items-center justify-between gap-2 bg-muted/50 border border-border rounded-lg px-3 py-2" data-testid={`invite-product-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Package className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                          <span className="text-sm font-medium truncate">{p.product_name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          ${(p.unit_amount / 100).toFixed(2)} {p.currency?.toUpperCase()}
                          {p.recurring ? ` / ${p.recurring.interval}` : ' (one-time)'}
                          <span className="ml-1.5 font-mono text-[10px] text-muted-foreground/60">{p.price_id}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setAdditionalProducts(prev => prev.filter((_, i) => i !== idx)); setCurrencyValidation(null); }}
                        className="p-1 text-muted-foreground hover:text-red-400 shrink-0"
                        data-testid={`invite-product-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSendSubscriptionInvite}
              disabled={inviteLoading}
              className="bg-[#ffd108] hover:bg-[#e6bc00] text-black font-medium"
              data-testid="invite-send-confirm-btn"
            >
              {inviteLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</> : <><Send className="h-4 w-4 mr-2" /> Send Invite</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Confirm"
      />
};

export default TenantDetail;
